/**
 * ISSUE-1042 R1: fills the `contests` settings slot. Lists the pool's
 * contests in the current season only, because /contest/list defaults to it,
 * with name, dates and status, and a "New contest" button that opens the New
 * contest dialog mounted by `PoolScreenPane`. Renders no card shell or title:
 * `PoolSettingsCardSlot` owns those.
 *
 * ISSUE-1330: a row the server marks `canDelete` (an upcoming contest with no
 * round yet, not the season's only one, and the viewer runs the pool) offers
 * Delete, behind the shared destructive confirm. Only the tapped row's button
 * shows the pending state.
 */
import {
  useContestDelete,
  useContestList,
} from "@sportsmet/contest-sdk/private";
import {
  Button,
  confirmDestructive,
  InlineBanner,
  Skeleton,
} from "@sportsmet/ui";
import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";

import {
  extractErrorCode,
  showApiError,
  showOfflineError,
} from "../../errors/apiErrorBus";
import { useOverlay } from "../../routeState/useOverlay";
import { NEW_CONTEST_DIALOG_NAME } from "../newContest/newContestModel";
import {
  contestDateRangeLabel,
  contestDeleteButtonLabel,
  contestDeleteConfirmTitle,
  CONTESTS_CARD_DELETE_BODY,
  CONTESTS_CARD_DELETE_CANCEL_LABEL,
  CONTESTS_CARD_DELETE_CONFIRM_LABEL,
  CONTESTS_CARD_DELETE_LABEL,
  CONTESTS_CARD_DELETING_LABEL,
  CONTESTS_CARD_EMPTY_MESSAGE,
  CONTESTS_CARD_FAILED_MESSAGE,
  CONTESTS_CARD_LOADING_LABEL,
  CONTESTS_CARD_NEW_LABEL,
  CONTESTS_CARD_RETRY_LABEL,
  contestStatusLabel,
} from "./contestsCardModel";

// The reads a deleted contest changes, refreshed by hand so the row leaves
// the moment the delete lands rather than when the hook's own invalidation
// runs.
const CONTEST_QUERY_KEY_PREFIXES = [
  "post:contest:contestList",
  "post:contest:contestGet",
];

export type typePoolSettingsContestsCardProps = { poolId: string };

export const PoolSettingsContestsCard = ({
  poolId,
}: typePoolSettingsContestsCardProps): ReactNode => {
  const overlay = useOverlay();
  const queryClient = useQueryClient();
  const contests = useContestList({ poolId });
  const deleteContest = useContestDelete();
  const [deletingContestId, setDeletingContestId] = useState<string | null>(
    null,
  );
  // No error modal for this read: the retry banner is the answer, as in
  // PoolStandingsPane for the same read.
  const isFailed = contests.isError || extractErrorCode(contests.data) !== null;
  const items = contests.data?.data?.items ?? [];

  async function handleDelete(item: {
    contestId: string;
    name: string;
  }): Promise<void> {
    const isConfirmed = await confirmDestructive({
      title: contestDeleteConfirmTitle(item.name),
      body: CONTESTS_CARD_DELETE_BODY,
      confirmLabel: CONTESTS_CARD_DELETE_CONFIRM_LABEL,
      cancelLabel: CONTESTS_CARD_DELETE_CANCEL_LABEL,
    });
    if (!isConfirmed) return;

    setDeletingContestId(item.contestId);
    try {
      const result = await deleteContest.mutateAsync({
        poolId,
        contestId: item.contestId,
      });
      if (showApiError(result)) return;
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const first = query.queryKey[0];
          return (
            typeof first === "string" &&
            CONTEST_QUERY_KEY_PREFIXES.includes(first)
          );
        },
      });
    } catch (error) {
      if (!showApiError(error)) showOfflineError();
    } finally {
      setDeletingContestId(null);
    }
  }

  if (contests.isLoading) {
    return (
      <div data-testid="pool-settings-contests-card">
        <div data-testid="pool-settings-contests-loading">
          <Skeleton shape="card" label={CONTESTS_CARD_LOADING_LABEL} />
        </div>
      </div>
    );
  }

  if (isFailed) {
    return (
      <div data-testid="pool-settings-contests-card">
        <div data-testid="pool-settings-contests-failed">
          <InlineBanner
            variant="warning"
            message={CONTESTS_CARD_FAILED_MESSAGE}
            action={{
              label: CONTESTS_CARD_RETRY_LABEL,
              onAction: () => {
                void contests.refetch();
              },
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-3"
      data-testid="pool-settings-contests-card"
    >
      {items.length === 0 ? (
        <p
          className="text-body text-ink-secondary"
          data-testid="pool-settings-contests-empty"
        >
          {CONTESTS_CARD_EMPTY_MESSAGE}
        </p>
      ) : (
        <ul
          className="flex flex-col gap-2"
          data-testid="pool-settings-contests-list"
        >
          {items.map((item) => (
            <li
              key={item.contestId}
              data-testid="pool-settings-contest-row"
              data-contest-id={item.contestId}
              className="flex items-start justify-between gap-3"
            >
              <div className="flex min-w-0 flex-col gap-1">
                <p className="text-body-strong text-ink">{item.name}</p>
                <p className="text-caption text-ink-secondary">
                  {contestDateRangeLabel(item)} ·{" "}
                  {contestStatusLabel(item.status)}
                </p>
              </div>
              {item.canDelete ? (
                <Button
                  variant="secondary"
                  size="sm"
                  aria-label={contestDeleteButtonLabel(item.name)}
                  loading={deletingContestId === item.contestId}
                  loadingLabel={CONTESTS_CARD_DELETING_LABEL}
                  disabled={deletingContestId === item.contestId}
                  onClick={() => {
                    void handleDelete(item);
                  }}
                  data-testid="pool-settings-contest-delete"
                >
                  {CONTESTS_CARD_DELETE_LABEL}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      <div>
        <Button
          variant="secondary"
          onClick={() => {
            overlay.open({ kind: "dialog", name: NEW_CONTEST_DIALOG_NAME });
          }}
          data-testid="pool-settings-contests-new"
        >
          {CONTESTS_CARD_NEW_LABEL}
        </Button>
      </div>
    </div>
  );
};
